// TransactionSummary.tsx
import React, { useState, useEffect } from 'react';
import { Form, InputGroup, Row, Col, Button } from 'react-bootstrap';
import { formatCurrency } from '../../../../../../../../../../utils/functions';
import { PaymentTransactionSummary } from '../../../../types';
import {
  FaTrash,
  FaLink,
  FaDiagramProject,
  FaArrowRotateLeft
} from 'react-icons/fa6';
import { FaMousePointer } from 'react-icons/fa';
import './TransactionSummary.scss';
import HeaderStats from './components/HeaderStats/HeaderStats';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { toast } from 'react-toastify';

interface TransactionSummaryProps {
  transactionSummary: PaymentTransactionSummary | null;
  userReportedData: any;
  handleReportedAmountChange: any;
  setTransactionDifference: any;
  onFinalTransactionSummaryChange: (finalSummary: any) => void;
  showBalances: boolean;
  missingInputs?: string[];
}

const TransactionSummary: React.FC<TransactionSummaryProps> = ({
  transactionSummary,
  userReportedData,
  handleReportedAmountChange,
  setTransactionDifference,
  onFinalTransactionSummaryChange,
  showBalances,
  missingInputs = [],
}) => {
  const [localSummary, setLocalSummary] = useState<any>(transactionSummary?.type_summary || {});
  const [originalSummary, setOriginalSummary] = useState<any>(transactionSummary?.type_summary || {});
  const [combiningMode, setCombiningMode] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [branchedTags, setBranchedTags] = useState<{ [key: string]: { tag: string; net_amount: number }[] }>({});
  const [hasCombined, setHasCombined] = useState(false);

  // Validate and sanitize numeric input
  const handleNumericInput = (value: string): number | '' => {
    if (value === '' || value === '-') return '';
    // Allow only numbers, one decimal point, and optional negative sign
    const sanitized = value.replace(/[^0-9.-]/g, '');
    // Prevent multiple decimal points
    const parts = sanitized.split('.');
    const cleaned = parts.length > 2 ? parts[0] + '.' + parts.slice(1).join('') : sanitized;
    // Prevent multiple negative signs and ensure it's only at the start
    const final = cleaned.replace(/(?!^)-/g, '').replace(/^-+/, '-');
    const num = parseFloat(final);
    return isNaN(num) ? '' : num;
  };

  useEffect(() => {
    if (transactionSummary) {
      setLocalSummary(transactionSummary.type_summary);
      setOriginalSummary(transactionSummary.type_summary);
      setSelectedTypes([]);
      setBranchedTags({});
      setHasCombined(false);
    }
  }, [transactionSummary]);

  const calculateDifference = (actual: number, reported: number) => reported - actual;

  // Whenever localSummary, branchedTags or userReportedData change, compute final summary and report to parent.
  useEffect(() => {
    if (transactionSummary) {
      const finalSummary = Object.entries(localSummary)
        // Exclude the "Efectivo" type
        .filter(([type]) => type !== "Efectivo")
        .reduce((acc, [type, data]) => {
          const reported = userReportedData[type] || 0;
          const tags = branchedTags[type] || [];
          // Calculate net amount (total - tip)
          const netAmount = (data as any).net_amount || ((data as any).amount - (data as any).tip_amount || 0);
          acc[type] = {
            payment_type_name: (data as any).name || type,
            system: netAmount,
            reported: reported,
            matched: Math.abs(netAmount - reported) < (netAmount * 0.0001),
            tags,
          };
          return acc;
        }, {} as any);
      onFinalTransactionSummaryChange(finalSummary);
    }
  }, [localSummary, branchedTags, userReportedData, transactionSummary, onFinalTransactionSummaryChange]);

  // Update the total transaction difference whenever userReportedData or branchedTags change
  useEffect(() => {
    if (transactionSummary) {
      const totalDifference = Object.entries(localSummary)
        .filter(([type]) => type !== 'Efectivo')
        .reduce((acc, [type, data]: [string, any]) => {
          const reported = userReportedData[type] || 0;
          // Calculate net amount (total - tip)
          const netAmount = data.net_amount || (data.amount - (data.tip_amount || 0));
          const difference = calculateDifference(netAmount, reported);
          return acc + (Math.abs(difference) < netAmount * 0.0001 ? 0 : difference);
        }, 0);
      setTransactionDifference(totalDifference);
    }
  }, [transactionSummary, userReportedData, branchedTags, localSummary, setTransactionDifference]);

  const handleCombineRows = () => {
    const combinedName = selectedTypes.join(' + ');
    // Calculate combined amounts (net, total, and tips)
    const combinedData = selectedTypes.reduce((acc, type) => {
      const netAmount = localSummary[type].net_amount || (localSummary[type].amount - (localSummary[type].tip_amount || 0));
      const totalAmount = localSummary[type].amount || localSummary[type].net_amount;
      const tipAmount = localSummary[type].tip_amount || 0;
      
      return {
        net_amount: acc.net_amount + netAmount,
        amount: acc.amount + totalAmount,
        tip_amount: acc.tip_amount + tipAmount
      };
    }, { net_amount: 0, amount: 0, tip_amount: 0 });
    
    const updatedSummary = { ...localSummary };
    selectedTypes.forEach((type) => delete updatedSummary[type]);
    updatedSummary[combinedName] = { 
      count: selectedTypes.length, 
      net_amount: combinedData.net_amount,
      amount: combinedData.amount,
      tip_amount: combinedData.tip_amount,
      name: combinedName 
    };
    setLocalSummary(updatedSummary);
    setSelectedTypes([]);
    setCombiningMode(false);
    setHasCombined(true);
    handleReportedAmountChange(combinedName, 0);
  };

  const handleUndoCombine = () => {
    setLocalSummary(originalSummary);
    setBranchedTags({});
    setHasCombined(false);
    // Reset all reported amounts for original types
    Object.keys(originalSummary)
      .filter(type => type !== 'Efectivo')
      .forEach(type => handleReportedAmountChange(type, ''));
  };

  const handleAddTag = (type: string) => {
    const updatedTags = branchedTags[type] || [];
    
    // Verificar si hay tags sin etiqueta asignada
    const hasEmptyTag = updatedTags.some(tag => !tag.tag || tag.tag.trim() === '');
    if (hasEmptyTag) {
      toast.warning('Debes asignar una etiqueta a todos los detalles anteriores antes de agregar uno nuevo');
      return;
    }
    
    setBranchedTags({
      ...branchedTags,
      [type]: [...updatedTags, { tag: '', net_amount: 0 }],
    });
  };

  const handleRemoveTag = (type: string, index: number) => {
    // Copiamos el array y eliminamos el tag en la posición indicada
    const updatedTags = [...(branchedTags[type] || [])];
    updatedTags.splice(index, 1);

    if (updatedTags.length === 0) {
      // Si ya no quedan tags, lo borramos del estado y notificamos 0
      const newBranched = { ...branchedTags };
      delete newBranched[type];
      setBranchedTags(newBranched);
      handleReportedAmountChange(type, 0);
    } else {
      // Actualizamos el estado con los tags restantes
      setBranchedTags({ ...branchedTags, [type]: updatedTags });
      // Recalculamos la suma y la notificamos al padre
      const newSum = updatedTags.reduce((sum, t) => sum + (t.net_amount || 0), 0);
      handleReportedAmountChange(type, newSum);
    }
  };


  const handleTagChange = (type: string, index: number, key: 'tag' | 'amount', value: string | number) => {
    const updatedTags = [...(branchedTags[type] || [])];
    // Map 'amount' to 'net_amount' for the actual property name
    if (key === 'amount') {
      updatedTags[index] = { ...updatedTags[index], net_amount: value as number };
    } else {
      updatedTags[index] = { ...updatedTags[index], tag: value as string };
    }
    setBranchedTags({ ...branchedTags, [type]: updatedTags });
    const reportedAmount = updatedTags.reduce((acc, tag) => acc + (tag.net_amount || 0), 0);
    handleReportedAmountChange(type, reportedAmount);
  };


  return (
    <div className="transaction-summary-section">
      {transactionSummary && (
        <>
          <HeaderStats transactionSummary={transactionSummary} showBalances={showBalances} />
          <h6>Totales por medio de pago</h6>
          <Row className="controls-row">
            <Col>
              <Button
                variant="none" className="mp-button light"
                onClick={() => setCombiningMode(!combiningMode)}
              >
                <FaMousePointer /> {combiningMode ? 'Cancelar selección' : 'Seleccionar'}  
              </Button>
            </Col>
            <Col className="text-end">
              {combiningMode && selectedTypes.length > 1 && (
                <Button variant="none" className="mp-button combinar-button" onClick={handleCombineRows}>
                  Combinar <FaLink />
                </Button>
              )}
              {!combiningMode && hasCombined && (
                <Button variant="none" className="mp-button combinar-button" onClick={handleUndoCombine}>
                  Deshacer <FaArrowRotateLeft />
                </Button>
              )}
            </Col>
          </Row>
          
          <div className="payment-methods-container">
            {Object.entries(localSummary)
              .filter(([type]) => type !== 'Efectivo')
              .map(([type, data]: [string, any]) => {
                const reported = userReportedData[type] || 0;
                // Calculate net amount (total - tip) for balancing
                const netAmount = data.net_amount || (data.amount - (data.tip_amount || 0));
                const difference = calculateDifference(netAmount, reported);
                const EPS = 0.01;
                const displayDiff = Math.abs(difference) < EPS ? 0 : difference;
                const isSelected = selectedTypes.includes(type);
                
                return (
                  <div 
                    key={type} 
                    className={`payment-method-card ${isSelected ? 'selected' : ''} ${combiningMode ? 'selectable' : ''}`}
                    onClick={combiningMode ? (e) => {
                      // Prevent selection when clicking inside input fields or buttons
                      if ((e.target as HTMLElement).closest('.tag-section, .input-section, .payment-actions')) {
                        return;
                      }
                      setSelectedTypes((prev) =>
                        prev.includes(type)
                          ? prev.filter((t) => t !== type)
                          : [...prev, type]
                      );
                    } : undefined}
                    style={{ cursor: combiningMode ? 'pointer' : 'default' }}
                  >
                    <div className="payment-method-content">
                      <div className="payment-type-column">
                        <div className="payment-type">{type}</div>
                      </div>
                      
                      <div className="system-amount">
                        <div className="amount-label">Sistema</div>
                        <div className="amount-value">
                          {!showBalances ? (
                            <span className="blur-balance">***</span>
                          ) : (
                            <>
                              <div className="system-total">
                                {formatCurrency(data.amount || data.net_amount)}
                              </div>
                              {(data.amount || data.tip_amount) && (
                                <div className="system-breakdown">
                                  <div className="breakdown-item">
                                    <span className="breakdown-label">Venta:</span>
                                    <span className="breakdown-value">
                                      {formatCurrency((data.amount || data.net_amount) - (data.tip_amount || 0))}
                                    </span>
                                  </div>
                                  {data.tip_amount > 0 && (
                                    <div className="breakdown-item">
                                      <span className="breakdown-label">Propina:</span>
                                      <span className="breakdown-value">
                                        {formatCurrency(data.tip_amount || 0)}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      
                      <div className="input-section">
                        <InputGroup className="amount-input-group">
                          <InputGroup.Text>$</InputGroup.Text>
                          <Form.Control
                            type="text"
                            className={`amount-input ${missingInputs.includes(type) ? 'input-missing' : ''}`}
                            placeholder="Monto"
                            value={userReportedData[type] ?? ''}
                            onChange={(e) => {
                              const validatedValue = handleNumericInput(e.target.value);
                              handleReportedAmountChange(type, validatedValue);
                            }}
                            disabled={branchedTags[type]?.length > 0}
                          />
                        </InputGroup>
                        {showBalances && (
                          <OverlayTrigger
                            placement="top"
                            overlay={<Tooltip>Igualar al sistema</Tooltip>}
                          >
                            <Button
                              variant="none"
                              className="mp-button light equal-button"
                              onClick={() => handleReportedAmountChange(type, netAmount)}
                              disabled={branchedTags[type]?.length > 0}
                              title="Igualar al sistema"
                            >
                              =
                            </Button>
                          </OverlayTrigger>
                        )}
                      </div>
                      
                      <div className="difference-section">
                        <div className="difference-label">Diferencia</div>
                        <div className={`difference-value ${!showBalances ? '' : (Math.abs(displayDiff) < EPS ? 'difference-green' : (displayDiff > 0 ? 'difference-green' : 'difference-red'))}`}>
                          {!showBalances ? (
                            <span className="blur-balance">***</span>
                          ) : (
                            formatCurrency(displayDiff)
                          )}
                        </div>
                      </div>
                      
                      <div className="payment-actions">
                        <Button 
                          variant="none" 
                          className="mp-button light" 
                          onClick={() => handleAddTag(type)}
                          title="Agregar detalle"
                        >
                          <FaDiagramProject />
                        </Button>
      
                      </div>
                    </div>

                    {branchedTags[type] && branchedTags[type].length > 0 && (
                      <div className="tag-section" onClick={(e) => e.stopPropagation()}>
                        {branchedTags[type].map((tag, index) => (
                          <div key={`${type}_tag_${index}`} className="tag-item">
                            <Form.Control
                              type="text"
                              className="tag-input"
                              placeholder="Etiqueta"
                              value={tag.tag}
                              onChange={(e) => handleTagChange(type, index, 'tag', e.target.value)}
                            />
                            <div className="tag-amount-input">
                              <InputGroup className="amount-input-group">
                                <InputGroup.Text>$</InputGroup.Text>
                                <Form.Control
                                  type="text"
                                  placeholder="Monto"
                                  className="amount-input"
                                  value={tag.net_amount || ''}
                                  onChange={(e) => {
                                    const validatedValue = handleNumericInput(e.target.value);
                                    handleTagChange(type, index, 'amount', validatedValue);
                                  }}
                                />
                              </InputGroup>
                            </div>
                            <button 
                              className="tag-remove-btn" 
                              onClick={() => handleRemoveTag(type, index)}
                              title="Eliminar etiqueta"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </>
      )}
    </div>
  );
};

export default TransactionSummary;
